// ./src/components/Countdown.jsx
import React from "react";
import PropTypes from "prop-types";

/**
 * Reusable countdown.
 * - Ticks every second
 * - Cleans up on unmount
 * - Accepts Date, ISO string, or {year, month0, day, hour, minute, second}
 * - Pure UI; parent decides positioning (overlay vs inline)
 */
export default function Countdown({ target, labels, className, style, onComplete }) {

    const targetDate = React.useMemo(() => {
        if (target instanceof Date) return target;
        if (typeof target === "string") return new Date(target); // ISO recommended
        if (typeof target === "object" && target && "year" in target) {
            const { year, month0, day, hour = 0, minute = 0, second = 0 } = target;
            return new Date(year, month0, day, hour, minute, second);
        }
        // Fallback to now so it shows 00:00:00:00 if misconfigured
        return new Date();
    }, [target]);

    const [timeLeft, setTimeLeft] = React.useState({
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
    });

    React.useEffect(() => {
        const pad = (n) => (n < 10 ? "0" + n : String(n));

        const compute = () => {
            const diff = targetDate - new Date();
            if (diff <= 0) {
                setTimeLeft({ days: "00", hours: "00", minutes: "00", seconds: "00" });
                if (onComplete) onComplete();
                return "done";
            }
            const totalSec = Math.floor(diff / 1000);
            const days = Math.floor(totalSec / 86400);
            const hours = Math.floor((totalSec % 86400) / 3600);
            const minutes = Math.floor((totalSec % 3600) / 60);
            const seconds = totalSec % 60;
            setTimeLeft({
                days: pad(days),
                hours: pad(hours),
                minutes: pad(minutes),
                seconds: pad(seconds),
            });
            return "tick";
        };

        if (compute() === "done") return;
        const id = setInterval(() => {
            if (compute() === "done") clearInterval(id);
        }, 1000);
        return () => clearInterval(id);
    }, [targetDate, onComplete]);

    // Basic styles; override via className/style as needed
    const base = {
        color: "#fff",
        fontFamily:
            "'Arial Black', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji'",
    };
    const grid = {
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(56px, 1fr))",
        gap: 8,
    };
    const cell = {
        background: "rgba(255,255,255,0.12)",
        borderRadius: 6,
        padding: "8px 6px",
        textAlign: "center",
        backdropFilter: "blur(2px)",
    };
    const val = { fontWeight: 900, lineHeight: 1, fontSize: "clamp(22px, 6vw, 48px)" };
    const label = {
        fontSize: "clamp(9px, 1.8vw, 12px)",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        opacity: 0.9,
        marginTop: 4,
    };

    const L = { ...Countdown.defaultProps.labels, ...labels };

    return (
        <div className={className} style={{ ...base, ...style }} aria-live="polite">
            <div style={grid}>
                <div style={cell}>
                    <div style={val}>{timeLeft.days}</div>
                    <div style={label}>{L.days}</div>
                </div>
                <div style={cell}>
                    <div style={val}>{timeLeft.hours}</div>
                    <div style={label}>{L.hours}</div>
                </div>
                <div style={cell}>
                    <div style={val}>{timeLeft.minutes}</div>
                    <div style={label}>{L.minutes}</div>
                </div>
                <div style={cell}>
                    <div style={val}>{timeLeft.seconds}</div>
                    <div style={label}>{L.seconds}</div>
                </div>
            </div>
        </div>
    );
}

Countdown.propTypes = {
    target: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string, // ISO date
        PropTypes.shape({
            year: PropTypes.number.isRequired,
            month0: PropTypes.number.isRequired, // 0-based
            day: PropTypes.number.isRequired,
            hour: PropTypes.number,
            minute: PropTypes.number,
            second: PropTypes.number,
        }),
    ]).isRequired,
    labels: PropTypes.shape({
        days: PropTypes.string,
        hours: PropTypes.string,
        minutes: PropTypes.string,
        seconds: PropTypes.string,
    }),
    className: PropTypes.string,
    style: PropTypes.object,
    onComplete: PropTypes.func,
};

Countdown.defaultProps = {
    labels: { days: "Giorni", hours: "Ore", minutes: "Minuti", seconds: "Secondi" },
    className: "",
    style: {},
    onComplete: undefined,
};